import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState, Flex, Icon, Text, Title } from '@kognitos/lattice';

import type {
  Commonv1Value,
  V1AutomationConfig,
  V1CodeManifest,
  V1CredentialInfo,
  V1Step,
} from '@/generated/hooks/grimoire/models';
import { groupStepsByPhase } from '@/modules/automation/utils/group-steps-by-phase';
import type { TStepRunStatus } from '@/modules/automation/utils/step-run-status';
import type { IStepRunIO } from '@/modules/automation/hooks/useFocusedTestRunStatus';
import type { TResolvedValue } from '@/modules/outputs/types';

import { ManifestClampedText } from './ManifestClampedText';
import { ManifestIOTable } from './ManifestIOTable';
import { ManifestStepCard, STEP_BODY_ANIM_MS } from './ManifestStepCard';

interface IManifestSopViewProps {
  manifest: V1CodeManifest;
  /** Heading for the document, used when nothing above it already names the
   *  automation. Surfaces that render `AutomationDocumentTitleBar` pass
   *  `showTitle={false}` instead — the bar is the only place the name belongs
   *  there, and two headings for one document read as two documents. */
  title?: string;
  /** Renders `title` as the document's own heading. */
  showTitle?: boolean;
  /** Automation-level business-tunable config, keyed by name — lives on the
   *  automation itself, not the manifest. Threaded down to each step so it
   *  can resolve and edit the configs it reads. */
  automationConfig?: V1AutomationConfig;
  /** Persists an edited config value. Omitted means no step shows a Config
   *  tab, regardless of whether it reads any config. */
  onSaveConfigValue?: (name: string, value: Commonv1Value) => Promise<void>;
  /** The automation's extracted code connections, keyed by connection
   *  alias — threaded down so each step can resolve its external calls'
   *  book/icon for its Connections tab. */
  credentialsByAlias?: ReadonlyMap<string, V1CredentialInfo>;
  /** Toggles a step as a context chip in the chat (Cursor "add to chat").
   *  Omitted hides the affordance on every step — e.g. when no chat is
   *  visible. */
  onToggleStepInChat?: (step: V1Step) => void;
  /** Whether a given step number is currently referenced in the chat
   *  composer — drives each step's toggle button state. */
  isStepReferencedInChat?: (stepNumber: number) => boolean;
  /** Live/historical test-run status for a given step number. Omitted
   *  (plain authoring view) renders every step's plain number. */
  getStepStatus?: (stepNumber: number) => TStepRunStatus | undefined;
  /** Real inputs/output/duration for a given step number, once it has
   *  executed in the focused test run. */
  getStepIO?: (stepNumber: number) => IStepRunIO | undefined;
  /** Resolves the focused test run's real value for a top-level automation
   *  input by name — added as a Value column directly on the Inputs
   *  schema table instead of a second, separate inputs block elsewhere. */
  getInputValue?: (name: string) => TResolvedValue | undefined;
  /** Same as `getInputValue`, for the automation's outputs — populated
   *  once the focused run completes. */
  getOutputValue?: (name: string) => TResolvedValue | undefined;
  /** Opens the step-test workbench for a step, identified by its 0-based
   *  ordinal in `manifest.steps` (the placeholder `stepId`). Omitted hides the
   *  per-step "Test step" button (e.g. when the workbench entry is disabled). */
  onTestStep?: (stepId: string) => void;
  /**
   * True when a finished focused run yielded no events. Every step's status and
   * I/O is then empty for lack of data, not because the run did nothing —
   * surfaced as one banner rather than silence on every step.
   */
  stepDetailsUnavailable?: boolean;
  /**
   * Whether the missing details are due to a fetch failure rather than the
   * run's journal genuinely coming back empty. Only changes the wording — there
   * is no retry action, because the reported case is a successful `200` with an
   * empty list, where re-fetching returns the same empty list.
   */
  stepDetailsFailed?: boolean;
}

// The overview reads as muted, secondary text against the title, not
// full-contrast body copy.
const OVERVIEW_TEXT_PROPS = { level: 'small', color: 'muted' } as const;

/** Renders a built automation's SOP as a phase-grouped, step-by-step view,
 *  sourced entirely from the typed `CodeManifest` — no markdown parsing.
 *  Each step expands and collapses independently: opening one never closes
 *  another, so two steps can be compared side by side and a step you opened
 *  stays open while you read on. */
export function ManifestSopView({
  manifest,
  title,
  showTitle = true,
  automationConfig,
  onSaveConfigValue,
  credentialsByAlias,
  onToggleStepInChat,
  isStepReferencedInChat,
  getStepStatus,
  getStepIO,
  getInputValue,
  getOutputValue,
  onTestStep,
  stepDetailsUnavailable = false,
  stepDetailsFailed = false,
}: IManifestSopViewProps) {
  const { t: tAutomation } = useTranslation('automation');
  const containerRef = useRef<HTMLDivElement>(null);
  // The workbench `stepId` is a step's 0-based ordinal in the flat manifest
  // step list (matching `resolveStep`), not its position within a phase group.
  const steps = manifest.steps ?? [];

  const phaseGroups = useMemo(
    () => groupStepsByPhase(manifest.steps),
    [manifest.steps],
  );

  // Steps the USER opened. A set (rather than one number) is what makes the
  // steps independent: opening one adds to this, it never replaces. The first
  // step opens by default so the view never looks empty.
  //
  // Kept separate from the run's followed step so the two never overwrite each
  // other: a step the user opened stays open until they close it, and the one
  // the run opened closes on its own when the run moves past it. Merged into a
  // single `isOpen` at the call site.
  const [userOpenedSteps, setUserOpenedSteps] = useState<ReadonlySet<number>>(
    () => {
      const firstNumber = manifest.steps?.[0]?.number;
      return new Set(firstNumber == null ? [] : [firstNumber]);
    },
  );

  // Set when the user closes the step the run is currently following. Without
  // it that step would reopen on the next render (it is open *because* it is
  // followed), so a click on it would do nothing — which is the one thing D1
  // exists to fix. Cleared whenever the run moves to a different step.
  const [dismissedFollowStep, setDismissedFollowStep] = useState<
    number | undefined
  >(undefined);

  // While a test run is focused (`getStepStatus` provided), the view follows
  // the run: a failed step wins, then the currently-running one, then a paused
  // (awaiting-guidance) one. Plain authoring view (no run focused) never
  // computes this, so manual clicks stay untouched there.
  let followStepNumber: number | undefined;
  if (getStepStatus) {
    const numberedSteps = steps.filter(
      (step): step is V1Step & { number: number } => step.number != null,
    );
    followStepNumber =
      numberedSteps.find(step => getStepStatus(step.number) === 'failed')
        ?.number ??
      numberedSteps.find(step => getStepStatus(step.number) === 'running')
        ?.number ??
      numberedSteps.find(step => getStepStatus(step.number) === 'paused')
        ?.number;
  }

  // eslint-disable-next-line no-restricted-syntax -- reason: opens the followed step + recenters the viewport as a live test run progresses (a UI reaction, not derivable state)
  useEffect(() => {
    // A new followed step is a fresh decision, so a dismissal of the previous
    // one does not carry over. Safe here because this only reruns when the
    // followed step actually changes, not when the user clicks.
    setDismissedFollowStep(undefined);
    if (followStepNumber == null) return;
    const stepNumber = followStepNumber;
    // Deliberately does NOT write to `userOpenedSteps`. The followed step is
    // open because it is followed, so it closes again by itself once the run
    // moves on — writing it into the set would leave every step the run ever
    // touched expanded for the rest of the session.

    const scrollToFollowedStep = () => {
      containerRef.current
        ?.querySelector(`[data-testid="manifest-step-card-${stepNumber}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };

    // Firing the recentering scroll in the same tick as the open toggle races
    // it against the step body's height animation: the scroll targets a
    // position computed once, but the layout keeps shifting under it as the
    // body expands, so neither finishes cleanly and the transition reads as an
    // instant snap. Waiting for the height animation to settle first lets the
    // scroll target a layout that's stopped moving.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      scrollToFollowedStep();
      return;
    }
    const timer = window.setTimeout(
      scrollToFollowedStep,
      STEP_BODY_ANIM_MS + 20,
    );
    return () => window.clearTimeout(timer);
  }, [followStepNumber]);

  const isFollowOpen =
    followStepNumber != null && followStepNumber !== dismissedFollowStep;

  /** A step is open if the user opened it, or the run is following it now. */
  const isStepOpen = (stepNumber: number) =>
    userOpenedSteps.has(stepNumber) ||
    (isFollowOpen && stepNumber === followStepNumber);

  const toggleStepOpen = (stepNumber: number) => {
    // Closing the followed step has to be recorded separately, or the follow
    // state reopens it immediately.
    if (stepNumber === followStepNumber && isStepOpen(stepNumber)) {
      setDismissedFollowStep(stepNumber);
      setUserOpenedSteps(current => {
        if (!current.has(stepNumber)) return current;
        const next = new Set(current);
        next.delete(stepNumber);
        return next;
      });
      return;
    }
    setUserOpenedSteps(current => {
      const next = new Set(current);
      // `delete` reports whether it removed anything, so one call covers both
      // directions without a separate `has` check.
      if (!next.delete(stepNumber)) next.add(stepNumber);
      return next;
    });
  };

  const inputs = manifest.inputs ?? [];
  const outputs = manifest.outputs ?? [];
  const hasSteps = steps.length > 0;

  return (
    // `dir="auto"` on the whole view, resolved from its first strong
    // directional character — the manifest title, i.e. authored content. Per
    // block isn't enough here the way it is for the markdown editor: these are
    // flex containers with `align-items: flex-start`, so an RTL block's box
    // shrink-wraps at the container's left edge and no amount of text-align
    // moves it. The container's own direction is what places its children.
    <Flex
      ref={containerRef}
      dir="auto"
      direction="column"
      // Full width so the content sits on the pane's own start edge: the view
      // is a flex item of an LTR parent with `align-items: flex-start`, so a
      // shrink-wrapped box would hug the left and leave RTL content stranded
      // there with empty space to its right.
      width="full"
      // min-w-0 lets a wide table scroll in its own wrapper instead of
      // widening this box past the pane. Padding, content width and the
      // gap between sections come from the shared layout tokens.
      className="automation-document-body automation-document-content min-w-0"
    >
      {/* Suppressed by surfaces that render the document title bar — see
          `showTitle`. */}
      {showTitle && (manifest.title || title) && (
        <Title level="h2" className="font-semibold">
          {manifest.title || title}
        </Title>
      )}

      {manifest.overview && (
        <Flex
          direction="column"
          // `break-words` (inherited by the markdown below) so a long
          // unbreakable token in the overview wraps inside the content column
          // rather than being clipped at its edge.
          className="automation-document-section w-full min-w-0 break-words"
        >
          <span className="automation-section-eyebrow">
            {tAutomation('automation.manifestSopView.description')}
          </span>
          <ManifestClampedText
            text={manifest.overview}
            textProps={OVERVIEW_TEXT_PROPS}
          />
        </Flex>
      )}

      {/* One banner for the whole view. Without it, a run whose journal came
          back empty leaves every step silently blank while still showing a
          green check — a success claim with nothing behind it. */}
      {stepDetailsUnavailable && (
        <Flex
          gap={2}
          align="center"
          className="w-full rounded-md border border-border p-3"
          data-testid="manifest-step-details-unavailable"
        >
          <Icon type="CircleAlert" size="sm" className="shrink-0" />
          <Flex direction="column" className="min-w-0 flex-1">
            <Text level="small" weight="medium">
              {tAutomation('automation.manifestSopView.runEvents.errorTitle')}
            </Text>
            <Text level="xSmall" color="muted">
              {tAutomation(
                stepDetailsFailed
                  ? 'automation.manifestSopView.runEvents.errorDescription'
                  : 'automation.manifestSopView.runEvents.emptyDescription',
              )}
            </Text>
          </Flex>
        </Flex>
      )}

      <ManifestIOTable
        title={tAutomation('automation.manifestSopView.inputs')}
        items={inputs}
        getValue={getInputValue}
      />

      {hasSteps ? (
        <Flex
          direction="column"
          className="automation-document-section w-full min-w-0"
        >
          <div className="automation-document-sticky-header w-full min-w-0">
            <span className="automation-section-eyebrow">
              {tAutomation('automation.manifestSopView.steps')}
            </span>
          </div>
          {/* `width="full"`, not `Flex`'s default `w-auto`: an auto-width box
              here is shrink-to-fit, which grows past the content column when a
              step's min-content does (see the note on `ManifestStepCard`'s
              root). That widens the whole step list, not just the one step. */}
          <Flex direction="column" gap={3} width="full" className="min-w-0">
            {phaseGroups.map((group, index) => (
              <Fragment key={`${group.phase}-${index}`}>
                {group.phase && (
                  <div className="automation-document-sticky-header automation-document-sticky-header--phase w-full min-w-0">
                    <span className="automation-section-eyebrow">
                      {group.phase}
                    </span>
                  </div>
                )}
                {group.steps.map((step, stepIndex) => (
                  <ManifestStepCard
                    key={step.number}
                    step={step}
                    isOpen={
                      step.number !== undefined && isStepOpen(step.number)
                    }
                    onToggle={() => {
                      if (step.number !== undefined)
                        toggleStepOpen(step.number);
                    }}
                    onTestStep={
                      onTestStep && step.entry_function?.name
                        ? () => onTestStep(step.entry_function?.name ?? '')
                        : undefined
                    }
                    isLast={stepIndex === group.steps.length - 1}
                    automationConfig={automationConfig}
                    onSaveConfigValue={onSaveConfigValue}
                    credentialsByAlias={credentialsByAlias}
                    onToggleStepInChat={onToggleStepInChat}
                    isReferencedInChat={
                      step.number !== undefined &&
                      (isStepReferencedInChat?.(step.number) ?? false)
                    }
                    status={
                      step.number !== undefined
                        ? getStepStatus?.(step.number)
                        : undefined
                    }
                    io={
                      step.number !== undefined
                        ? getStepIO?.(step.number)
                        : undefined
                    }
                  />
                ))}
              </Fragment>
            ))}
          </Flex>
        </Flex>
      ) : (
        <EmptyState
          icon="Workflow"
          title={tAutomation('automation.manifestSopView.emptySteps.title')}
          description={tAutomation(
            'automation.manifestSopView.emptySteps.description',
          )}
        />
      )}

      <ManifestIOTable
        title={tAutomation('automation.manifestSopView.outputs')}
        items={outputs}
        getValue={getOutputValue}
      />
    </Flex>
  );
}
